import { v4 as uuid } from 'uuid';
import { catchError, mergeMap, throwError } from "rxjs";
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, Store, type StateContext } from '@ngxs/store';
import { Navigate } from '@ngxs/router-plugin';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import * as FlatStates from '../../internal';

export const HISTORY_CONTROL_TOKEN = new StateToken<HistoryControlStateModel>('historyControl');

export interface HistoryControlStateModel {
  blockUiType: FlatModels.EntityControlModels.BlockUiType,
  past: FlatModels.EntityControlModels.HistorySnapshot[];
  future: FlatModels.EntityControlModels.HistorySnapshot[];
  deleted: FlatModels.EntityControlModels.HistorySnapshot[];
  currentChanges: FlatModels.EntityControlModels.HistorySnapshot;
}

export const entityMappings: FlatModels.EntityControlModels.EntityMapping = {
  // Entities
  [FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_ENTITY]: {
    getEntitySuccess: FlatActions.AnnotationEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.AnnotationEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.AnnotationEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.AnnotationEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.AnnotationEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.AnnotationEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.AnnotationEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_GROUP_ENTITY]: {
    getEntitySuccess: FlatActions.AnnotationGroupEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.AnnotationGroupEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.AnnotationGroupEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.AnnotationGroupEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.AnnotationGroupEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.AnnotationGroupEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.AnnotationGroupEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_LABEL_ENTITY]: {
    getEntitySuccess: FlatActions.AnnotationLabelEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.AnnotationLabelEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.AnnotationLabelEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.AnnotationLabelEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.AnnotationLabelEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.AnnotationLabelEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.AnnotationLabelEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_SOURCE_ENTITY]: {
    getEntitySuccess: FlatActions.AnnotationSourceEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.AnnotationSourceEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.AnnotationSourceEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.AnnotationSourceEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.AnnotationSourceEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.AnnotationSourceEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.AnnotationSourceEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_TARGET_ENTITY]: {
    getEntitySuccess: FlatActions.AnnotationTargetEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.AnnotationTargetEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.AnnotationTargetEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.AnnotationTargetEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.AnnotationTargetEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.AnnotationTargetEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.AnnotationTargetEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.CHOICE_ENTITY]: {
    getEntitySuccess: FlatActions.ChoiceEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.ChoiceEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.ChoiceEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.ChoiceEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.ChoiceEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.ChoiceEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.ChoiceEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.COPYRIGHT_ENTITY]: {
    getEntitySuccess: FlatActions.CopyrightEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.CopyrightEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.CopyrightEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.CopyrightEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.FREE_FORM_STYLE_ENTITY]: {
    getEntitySuccess: FlatActions.FreeFormStyleEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.FreeFormStyleEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.FreeFormStyleEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.FreeFormStyleEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.FreeFormStyleEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.FreeFormStyleEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.FreeFormStyleEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.IMAGE_ENTITY]: {
    getEntitySuccess: FlatActions.ImageEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.ImageEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.ImageEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.ImageEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.ImageEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.ImageEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.ImageEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.KEYMAP_COLUMN_ENTITY]: {
    getEntitySuccess: FlatActions.KeymapColumnEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.KeymapColumnEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.KeymapColumnEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.KeymapColumnEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.KeymapColumnEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.KeymapColumnEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.KeymapColumnEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.KEYMAP_LABEL_ENTITY]: {
    getEntitySuccess: FlatActions.KeymapLabelEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.KeymapLabelEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.KeymapLabelEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.KeymapLabelEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.KeymapLabelEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.KeymapLabelEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.KeymapLabelEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.KEYMAP_STYLE_ENTITY]: {
    getEntitySuccess: FlatActions.KeymapStyleEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.KeymapStyleEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.KeymapStyleEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.KeymapStyleEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.KeymapStyleEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.KeymapStyleEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.KeymapStyleEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.LAYER_ENTITY]: {
    getEntitySuccess: FlatActions.SlideLayerEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.SlideLayerEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.SlideLayerEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.SlideLayerEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.SlideLayerEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.SlideLayerEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.SlideLayerEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.MEDIA_ENTITY]: {
    getEntitySuccess: FlatActions.MediaEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.MediaEntityActions.GetEntitiesSuccess,
    modifyEntity: true,
    modifyEntitySuccess: FlatActions.MediaEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.MediaEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.METADATA_ENTITY]: {
    getEntitySuccess: FlatActions.MetadataEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.MetadataEntityActions.GetEntitiesSuccess,
    modifyEntity: true,
    modifyEntitySuccess: FlatActions.MetadataEntityActions.GetEntitySuccess,
    modifyEntitiesSuccess: FlatActions.MetadataEntityActions.GetEntitiesSuccess,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.MODALITY_ENTITY]: {
    getEntitySuccess: FlatActions.ModalityEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.ModalityEntityActions.GetEntitiesSuccess,
    modifyEntity: true,
    modifyEntitySuccess: FlatActions.ModalityEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.ModalityEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.NODE_ENTITY]: {
    getEntitySuccess: FlatActions.NodeEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.NodeEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.NodeEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.NodeEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.NodeEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.NodeEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.NodeEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.ROOT_NODE_ENTITY]: {
    getEntitySuccess: FlatActions.NodeEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.NodeEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.NodeEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.NodeEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.NodeEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.NodeEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.NodeEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.PAGE_ENTITY]: {
    getEntitySuccess: FlatActions.PageEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.PageEntityActions.GetEntitiesSuccess,
    modifyEntity: true,
    modifyEntitySuccess: FlatActions.PageEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.PageEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.PageEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.PageEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.PageEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.POINT_OF_INTEREST_ENTITY]: {
    getEntitySuccess: FlatActions.PointOfInterestEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.PointOfInterestEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.PointOfInterestEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.PointOfInterestEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.PointOfInterestEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.PointOfInterestEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.PointOfInterestEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.QUESTION_ENTITY]: {
    getEntitySuccess: FlatActions.QuestionEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.QuestionEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.QuestionEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.QuestionEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.QuestionEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.QuestionEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.QuestionEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.STAIN_ENTITY]: {
    getEntitySuccess: FlatActions.StainEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.StainEntityActions.GetEntitiesSuccess,
    modifyEntity: true,
    modifyEntitySuccess: FlatActions.StainEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.StainEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.USER_ENTITY]: {
    getEntitySuccess: FlatActions.UserEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.UserEntityActions.GetEntitiesSuccess,
    modifyEntity: true,
    modifyEntitySuccess: FlatActions.UserEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.UserEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.VOCABULARY_GROUP_ENTITY]: {
    getEntitySuccess: FlatActions.VocabularyGroupEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.VocabularyGroupEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.VocabularyGroupEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.VocabularyGroupEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.VocabularyGroupEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.VocabularyGroupEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.VocabularyGroupEntityActions.CleanUpSnapshots,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.VOCABULARY_WORD_ENTITY]: {
    getEntitySuccess: FlatActions.VocabularyWordEntityActions.GetEntitySuccess,
    getEntitiesSuccess: FlatActions.VocabularyWordEntityActions.GetEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: FlatActions.VocabularyWordEntityActions.ModifyEntitySuccess,
    modifyEntitiesSuccess: FlatActions.VocabularyWordEntityActions.ModifyEntitiesSuccess,
    undoSnapshot: FlatActions.VocabularyWordEntityActions.UndoSnapshot,
    redoSnapshot: FlatActions.VocabularyWordEntityActions.RedoSnapshot,
    cleanUpSnapshots: FlatActions.VocabularyWordEntityActions.CleanUpSnapshots,
  },
  // Special
  [FlatModels.EntityControlModels.EntityQueryType.enum.DETACHED_PAGE]: {
    getEntitySuccess: FlatActions.PageControlActions.GetDetachedPageEntitySuccess,
    getEntitiesSuccess: FlatActions.PageControlActions.GetDetachedPageEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: null,
    modifyEntitiesSuccess: null,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.SLIDE_VIEWER_PAGE]: {
    getEntitySuccess: FlatActions.SlideControlActions.GetSlideEntitySuccess,
    getEntitiesSuccess: FlatActions.SlideControlActions.GetSlideEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: null,
    modifyEntitiesSuccess: null,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.TRAINING_VIEWER_SLIDE]: {
    getEntitySuccess: FlatActions.TrainingSharedActions.GetSlideEntitySuccess,
    getEntitiesSuccess: FlatActions.TrainingSharedActions.GetSlideEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: null,
    modifyEntitiesSuccess: null,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.TRAINING_VIEWER_BLOCK]: {
    getEntitySuccess: FlatActions.TrainingSharedActions.GetBlockEntitySuccess,
    getEntitiesSuccess: FlatActions.TrainingSharedActions.GetBlockEntitiesSuccess,
    modifyEntity: false,
    modifyEntitySuccess: null,
    modifyEntitiesSuccess: null,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.TEXT_EXPORT]: {
    getEntitySuccess: null,
    getEntitiesSuccess: FlatActions.TextExportAuthorActions.GetTextExportsSuccess,
    modifyEntity: false,
    modifyEntitySuccess: null,
    modifyEntitiesSuccess: null,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  },
  [FlatModels.EntityControlModels.EntityQueryType.enum.MEDIA_USED_IN]: {
    getEntitySuccess: null,
    getEntitiesSuccess: FlatActions.MediaAuthorActions.GetMediasUsedInSuccess,
    modifyEntity: false,
    modifyEntitySuccess: null,
    modifyEntitiesSuccess: null,
    undoSnapshot: null,
    redoSnapshot: null,
    cleanUpSnapshots: null,
  }
}

export class DefaultHistorySnapshot implements FlatModels.EntityControlModels.HistorySnapshot {
  id: string;
  url: string;
  fromUrl: string;
  saved: boolean;
  snapshotItems: FlatModels.EntityControlModels.HistorySnapshotItem[];

  constructor(public name: string = 'history.default') {
    this.id = uuid();
    this.url = "";
    this.fromUrl = "";
    this.saved = false;
    this.snapshotItems = [];
  }
}

@State<HistoryControlStateModel>({
  name: HISTORY_CONTROL_TOKEN,
  defaults: {
    blockUiType: FlatModels.EntityControlModels.BlockUiType.enum.none,
    past: [],
    future: [],
    deleted: [],
    currentChanges: new DefaultHistorySnapshot(),
  }
})
@Injectable()
export class HistoryControlState {
  constructor(protected store: Store,
              protected apiControlService: ApiControlService) {
  }

  //<editor-fold desc="Selectors">

  @Selector([HistoryControlState])
  static blockUiType$(state: HistoryControlStateModel) {
    return state.blockUiType;
  }
  
  @Selector([HistoryControlState])
  static pastSnapshots$(state: HistoryControlStateModel) {
    return state.past;
  }
  
  @Selector([HistoryControlState.pastSnapshots$])
  static pastSnapshotItems$(pastSnapshots: FlatModels.EntityControlModels.HistorySnapshot[]) {
    return pastSnapshots.reduce((arr, pastSnapshot) => {
      arr.push(...pastSnapshot.snapshotItems || []);
      return arr;
    }, [] as FlatModels.EntityControlModels.HistorySnapshotItem[]);
  }

  @Selector([HistoryControlState.pastSnapshots$])
  static unsavedPastSnapshots$(pastSnapshots: FlatModels.EntityControlModels.HistorySnapshot[]) {
    return pastSnapshots.filter((pastSnapshot) => pastSnapshot.saved === false);
  }

  @Selector([HistoryControlState.unsavedPastSnapshots$])
  static unsavedPastSnapshotItems$(unsavedPastSnapshots: FlatModels.EntityControlModels.HistorySnapshot[]) {
    return unsavedPastSnapshots.reduce((arr, unsavedPastSnapshot) => {
      arr.push(...unsavedPastSnapshot.snapshotItems || []);
      return arr;
    }, [] as FlatModels.EntityControlModels.HistorySnapshotItem[]);
  }

  @Selector([HistoryControlState.pastSnapshots$])
  static canUndo$(pastSnapshots: FlatModels.EntityControlModels.HistorySnapshot[]) {
    return pastSnapshots.length > 0;
  }

  @Selector([HistoryControlState])
  static futureSnapshots$(state: HistoryControlStateModel) {
    return state.future;
  }

  @Selector([HistoryControlState.futureSnapshots$])
  static futureSnapshotItems$(futureSnapshots: FlatModels.EntityControlModels.HistorySnapshot[]) {
    return futureSnapshots.reduce((arr, futureSnapshot) => {
      arr.push(...futureSnapshot.snapshotItems || []);
      return arr;
    }, [] as FlatModels.EntityControlModels.HistorySnapshotItem[]);
  }

  @Selector([HistoryControlState.futureSnapshots$])
  static savedFutureSnapshots$(futureSnapshots: FlatModels.EntityControlModels.HistorySnapshot[]) {
    return futureSnapshots.filter((futureSnapshot) => futureSnapshot.saved === true);
  }

  @Selector([HistoryControlState.savedFutureSnapshots$])
  static savedFutureSnapshotItems$(savedFutureSnapshots: FlatModels.EntityControlModels.HistorySnapshot[]) {
    return savedFutureSnapshots.reduce((arr, savedFutureSnapshot) => {
      arr.push(...savedFutureSnapshot.snapshotItems || []);
      return arr;
    }, [] as FlatModels.EntityControlModels.HistorySnapshotItem[]);
  }

  @Selector([HistoryControlState.futureSnapshots$])
  static canRedo$(futureSnapshot: FlatModels.EntityControlModels.HistorySnapshot[]) {
    return futureSnapshot.length > 0;
  }

  @Selector([
    HistoryControlState.pastSnapshotItems$,
    HistoryControlState.futureSnapshotItems$
  ])
  static snapshotItems$(pastSnapshotItems: FlatModels.EntityControlModels.HistorySnapshotItem[],
                        futureSnapshotItems: FlatModels.EntityControlModels.HistorySnapshotItem[]) {
    return [...pastSnapshotItems, ...futureSnapshotItems];
  }

  @Selector([
    HistoryControlState.unsavedPastSnapshotItems$,
    HistoryControlState.savedFutureSnapshotItems$
  ])
  static changedSnapshotItems$(unsavedPastSnapshotItems: FlatModels.EntityControlModels.HistorySnapshotItem[],
                               savedFutureSnapshotItems: FlatModels.EntityControlModels.HistorySnapshotItem[]) {
    return [...unsavedPastSnapshotItems, ...savedFutureSnapshotItems];
  }

  @Selector([HistoryControlState.changedSnapshotItems$])
  static canSave$(changedSnapshotItems: FlatModels.EntityControlModels.HistorySnapshotItem[]) {
    return changedSnapshotItems.length > 0;
  }

  @Selector([HistoryControlState.changedSnapshotItems$])
  static hasSnapshotItems$(changedSnapshotItems: FlatModels.EntityControlModels.HistorySnapshotItem[]) {
    return (ids: string[]) => {
      return changedSnapshotItems.some(si => {
        return ids.includes(si.entityId);
      }) || false;
    };
  }

  //</editor-fold>

  //<editor-fold desc="Actions">
  
  @Action(FlatActions.HistoryControlActions.SetBlockUiType)
  setBlockUiType({patchState}: StateContext<HistoryControlStateModel>,
                 {blockUiType}: FlatActions.HistoryControlActions.SetBlockUiType) {
    patchState({
      blockUiType
    });
  }

  @Action(FlatActions.HistoryControlActions.GetEntitySuccess)
  getEntitySuccess(state: StateContext<HistoryControlStateModel>,
                   {entityQueryResult}: FlatActions.HistoryControlActions.GetEntitySuccess) {
    const entityMapping = entityMappings[entityQueryResult.stateToken];
    if (entityMapping) {
      this.store.dispatch(new entityMapping.getEntitySuccess(entityQueryResult));
    }
  }

  @Action(FlatActions.HistoryControlActions.GetEntitiesSuccess)
  getEntitiesSuccess(state: StateContext<HistoryControlStateModel>,
                     {entityQueryResults}: FlatActions.HistoryControlActions.GetEntitiesSuccess) {
    // Format the results
    const entityTypesMap = new Map<string, FlatModels.EntityControlModels.EntityQueryResult[]>();
    for (const entityQueryResult of entityQueryResults) {
      if (entityTypesMap.has(entityQueryResult.stateToken)) {
        entityTypesMap.set(entityQueryResult.stateToken, [...(entityTypesMap.get(entityQueryResult.stateToken) as FlatModels.EntityControlModels.EntityQueryResult[]), entityQueryResult]);
      } else {
        entityTypesMap.set(entityQueryResult.stateToken, [entityQueryResult]);
      }
    }

    // Iterate through results
    for (const [type, entityQueryResults] of entityTypesMap) {
      const entityMapping = entityMappings[type];
      if (entityMapping) {
        this.store.dispatch(new entityMapping.getEntitiesSuccess(entityQueryResults));
      }
    }
  }

  @Action(FlatActions.HistoryControlActions.ModifyEntitySuccess)
  modifyEntitySuccess(state: StateContext<HistoryControlStateModel>,
                      {entityModifyResult}: FlatActions.HistoryControlActions.ModifyEntitySuccess) {
    const entityMapping = entityMappings[entityModifyResult.stateToken];
    if (entityMapping) {
      this.store.dispatch(new entityMapping.modifyEntitySuccess(entityModifyResult));
    }
  }

  @Action(FlatActions.HistoryControlActions.ModifyEntitiesSuccess)
  modifyEntitiesSuccess(state: StateContext<HistoryControlStateModel>,
                        {entityModifyResults}: FlatActions.HistoryControlActions.ModifyEntitiesSuccess) {
    // Format the results
    const entityTypesMap = new Map<string, FlatModels.EntityControlModels.SaveSnapshotItem[]>();
    for (const entityModifyResult of entityModifyResults) {
      if (entityTypesMap.has(entityModifyResult.stateToken)) {
        entityTypesMap.set(entityModifyResult.stateToken, [...(entityTypesMap.get(entityModifyResult.stateToken) as FlatModels.EntityControlModels.SaveSnapshotItem[]), entityModifyResult]);
      } else {
        entityTypesMap.set(entityModifyResult.stateToken, [entityModifyResult]);
      }
    }

    // Iterate through results
    for (const [type, entityModifyResults] of entityTypesMap) {
      const entityMapping = entityMappings[type];
      if (entityMapping) {
        this.store.dispatch(new entityMapping.modifyEntitiesSuccess(entityModifyResults));
      }
    }
  }

  @Action(FlatActions.HistoryControlActions.UndoSnapshot)
  undoSnapshot({getState, patchState}: StateContext<HistoryControlStateModel>) {
    // Get State
    let future: FlatModels.EntityControlModels.HistorySnapshot[] = structuredClone(getState().future);
    let past: FlatModels.EntityControlModels.HistorySnapshot[] = structuredClone(getState().past);

    // Check For Past
    if (past.length === 0) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.no_previous_snapshot_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.warning,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.HistoryControlState.undoSnapshot"
      );
    }

    // Get Snapshot To Revert
    const snapshotToRevert = structuredClone(past[0]);
    
    // Get Current Url
    const currentUrl = this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$);
    
    // Check Url
    if (snapshotToRevert && snapshotToRevert.url !== currentUrl) {
      this.store.dispatch(new Navigate(['/' + snapshotToRevert.url]));
      this.store.dispatch(new FlatActions.AlertControlActions.AddAlert({
        key: 'main',
        detail: 'notification.snapshot_to_revert_route_changed',
        severity: FlatModels.AlertControlModels.AlertType.enum.info,
        life: FlatModels.AlertControlModels.AlertTime.DEFAULT
      }));
    } else {
      // Update History
      future = [past[0], ...future];
      past = past.slice(1);
      
      // Undo snapshot in entities
      for (let i = snapshotToRevert.snapshotItems.length - 1; i >= 0; i--) {
        // Get Snapshot Item
        const snapshotItem = snapshotToRevert.snapshotItems[i];
        
        // Undo Snapshot
        const stateTokenName = (snapshotItem as any).stateToken.name;
        const entityMapping = entityMappings[stateTokenName];
        if (entityMapping) {
          this.store.dispatch(new entityMapping.undoSnapshot(snapshotItem)).subscribe({
            next: () => {
              if ([FlatModels.EntityControlModels.EntityQueryType.enum.LAYER_ENTITY.toString(), FlatModels.EntityControlModels.EntityQueryType.enum.IMAGE_ENTITY.toString()].includes(stateTokenName) && snapshotItem.modifierType === FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD) {
                this.store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(0)).pipe(
                  mergeMap(() =>
                    this.store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(0))
                  )
                ).subscribe({
                  error: (result) => {
                    return this.store.dispatch(new FlatActions.AlertControlActions.AddAlert({
                      key: 'main',
                      detail: result.error?.message || result.message,
                      severity: FlatModels.AlertControlModels.AlertType.enum.error,
                      life: FlatModels.AlertControlModels.AlertTime.DEFAULT
                    }));
                  },
                });
              }
            }
          });
        }
      }
      
      // Patch State
      patchState({
        future,
        past
      });
      
      // Navigate to Url
      if (snapshotToRevert && currentUrl !== snapshotToRevert.fromUrl) {
        this.store.dispatch(new Navigate(['/' + snapshotToRevert.fromUrl]));
      }
    }
  }

  @Action(FlatActions.HistoryControlActions.RedoSnapshot)
  redoSnapshot({getState, patchState}: StateContext<HistoryControlStateModel>) {
    // Get State
    let future: FlatModels.EntityControlModels.HistorySnapshot[] = structuredClone(getState().future);
    let past: FlatModels.EntityControlModels.HistorySnapshot[] = structuredClone(getState().past);

    // Check For Future
    if (future.length === 0) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.no_following_snapshot_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.warning,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.HistoryControlState.redoSnapshot"
      );
    }
    
    // Get Snapshot To Advance
    const snapshotToAdvance = structuredClone(future[0]);
    
    // Get Current Url
    const currentUrl = this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$);
    
    // Check Url
    if (snapshotToAdvance && snapshotToAdvance.fromUrl !== currentUrl) {
      this.store.dispatch(new Navigate(['/' + snapshotToAdvance.fromUrl]));
      this.store.dispatch(new FlatActions.AlertControlActions.AddAlert({
        key: 'main',
        detail: 'notification.snapshot_to_advance_route_changed',
        severity: FlatModels.AlertControlModels.AlertType.enum.info,
        life: FlatModels.AlertControlModels.AlertTime.DEFAULT
      }));
    } else {
      // Update History
      past = [future[0], ...past];
      future = future.slice(1);
      
      // Redo snapshot in entities
      for (let i = 0; i <= snapshotToAdvance.snapshotItems.length - 1; i++) {
        // Get Snapshot Item
        const snapshotItem = snapshotToAdvance.snapshotItems[i];
        
        // Redo Snapshot
        const entityMapping = entityMappings[(snapshotItem as any).stateToken.name];
        if (entityMapping) {
          this.store.dispatch(new entityMapping.redoSnapshot(snapshotItem)).subscribe();
        }
      }
      
      // Patch State
      patchState({
        future,
        past
      });
      
      // Navigate to Url
      if (snapshotToAdvance && currentUrl !== snapshotToAdvance.url) {
        this.store.dispatch(new Navigate(['/' + snapshotToAdvance.url]));
      }
    }
  }

  @Action(FlatActions.HistoryControlActions.FinishSnapshot)
  finishSnapshot({getState, patchState}: StateContext<HistoryControlStateModel>,
                 {name, url, fromUrl}: FlatActions.HistoryControlActions.FinishSnapshot) {
    // Get state
    const state = structuredClone(getState());

    // Check for snapshot items
    if (state.currentChanges.snapshotItems.length > 0) {
      // Set data
      state.currentChanges.name = name;
      state.currentChanges.url = url;
      state.currentChanges.fromUrl = fromUrl || url;

      // Check For Max Capacity In Store
      const past = state.past;
      // Store 200 Steps
      if (past.length >= 199) {
        // Get Clean Up Snapshot
        const cleanUpSnapshot = past.pop();
        if (cleanUpSnapshot) {
          // Get All State Tokens And Clean Up Snapshots
          const cleanUpStateTokens: Set<string> = new Set<string>();
          const cleanUpSnapshots = cleanUpSnapshot.snapshotItems.reduce((arr, cleanUpSnapshotItem) => {
            // Get State Token
            const stateToken = (cleanUpSnapshotItem as any).stateToken.name;

            // Add Clean Up State Token
            cleanUpStateTokens.add(stateToken);

            // Get State Token Clean Up Snapshot Ids
            const stateTokenCleanUpSnapshotIds = arr.get(stateToken) || new Set<string>();

            // Add Snapshot Ids
            if (cleanUpSnapshotItem.previousSnapshotId) {
              stateTokenCleanUpSnapshotIds.add(cleanUpSnapshotItem.previousSnapshotId);
            }
            if (cleanUpSnapshotItem.followingSnapshotId) {
              stateTokenCleanUpSnapshotIds.add(cleanUpSnapshotItem.followingSnapshotId);
            }

            // Add To Array
            arr.set(stateToken, stateTokenCleanUpSnapshotIds);

            // Return Map
            return arr;
          }, new Map<string, Set<string>>());

          // Get Current Snapshots
          const currentSnapshots = [
            ...past.slice(0, past.length),
            state.currentChanges,
            ...state.future,
            ...state.deleted
          ].reduce((arr, snapshot) => {
            // Iterate Through Snapshot Items
            snapshot.snapshotItems
              .filter(snapshotItem => Array.from(cleanUpStateTokens).includes((snapshotItem as any).stateToken.name))
              .forEach(snapshotItem => {
                // Get State Token
                const stateToken = (snapshotItem as any).stateToken.name;

                // Get State Token Snapshot Ids
                const stateTokenSnapshotIds = arr.get(stateToken) || new Set<string>();

                // Add Snapshot Ids
                if (snapshotItem.previousSnapshotId) {
                  stateTokenSnapshotIds.add(snapshotItem.previousSnapshotId);
                }
                if (snapshotItem.followingSnapshotId) {
                  stateTokenSnapshotIds.add(snapshotItem.followingSnapshotId);
                }

                // Add To Array
                arr.set(stateToken, stateTokenSnapshotIds);
              });

            // Return Map
            return arr;
          }, new Map<string, Set<string>>());

          // Remove Old Snapshots
          cleanUpSnapshots.forEach((cleanUpSnapshotIds, key) => {
            const currentSnapshotIds = currentSnapshots.get(key);
            if (currentSnapshotIds) {
              currentSnapshotIds.forEach(Set.prototype.delete, cleanUpSnapshotIds);
            }

            // Clean Up Snapshots
            const entityMapping = entityMappings[key];
            if (entityMapping) {
              this.store.dispatch(new entityMapping.cleanUpSnapshots(Array.from(cleanUpSnapshotIds)));
            }
          });
        } else {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            'notification.no_snapshot_found',
            FlatModels.ErrorControlModels.ErrorLevel.enum.critical,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatStates.HistoryControlState.finishSnapshot"
          );
        }
      }

      // Save Changes
      patchState({
        past: [state.currentChanges, ...past],
        currentChanges: new DefaultHistorySnapshot(),
      });
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.empty_snapshot',
        FlatModels.ErrorControlModels.ErrorLevel.enum.warning,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.HistoryControlState.finishSnapshot"
      );
    }
  }

  @Action(FlatActions.HistoryControlActions.SaveSnapshots)
  saveSnapshots({getState}: StateContext<HistoryControlStateModel>) {
    // Get state
    const state = getState();

    // Get entire States
    const entityStates = this.store.selectSnapshot(state => state);

    // Get the modified snapshots
    const typedModifiedSnapshots: { [id: string]: FlatModels.EntityControlModels.SaveSnapshotItem } = {};

    // Check Past
    state.past
      .filter(p => !p.saved)
      .reverse()
      .forEach((snapshot: FlatModels.EntityControlModels.HistorySnapshot) => {
        snapshot.snapshotItems.forEach((si) => {
          // Get Entity State
          const entityState = entityStates[(si as any).stateToken.name];

          // Check Modifier Type
          let snapshotData
          switch (si.modifierType) {
            case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD:
              snapshotData = entityState.snapshots[si.followingSnapshotId as string] as FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateItemModel;
              break;
            case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE:
              snapshotData = entityState.snapshots[si.followingSnapshotId as string] as FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateItemModel;
              break;
            case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE:
              snapshotData = entityState.snapshots[si.previousSnapshotId as string] as FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateItemModel;
              break;
            default:
              throw new FlatModels.ErrorControlModels.MedsurfError(
                "notification.type_not_supported",
                FlatModels.ErrorControlModels.ErrorLevel.enum.error,
                FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
                "LIBS.FlatStates.HistoryControlState.saveSnapshots"
              );
          }

          // Get Entity
          const entity = JSON.parse(snapshotData.itemJson);
          if (!entity) {
            throw new FlatModels.ErrorControlModels.MedsurfError(
              "notification.cannot_parse_entity",
              FlatModels.ErrorControlModels.ErrorLevel.enum.error,
              FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
              "LIBS.FlatStates.HistoryControlState.saveSnapshots"
            );
          }

          // Add entity to modified snapshots
          typedModifiedSnapshots[si.id] = {
            id: si.id,
            snapshotId: snapshot.id,
            stateToken: (si as any).stateToken.name as FlatModels.EntityControlModels.EntityQueryType,
            modifierType: si.modifierType,
            entity
          };
        });
      });

    // Check Future
    state.future
      .filter(f => f.saved)
      .reverse()
      .forEach((snapshot: FlatModels.EntityControlModels.HistorySnapshot) => {
        snapshot.snapshotItems.forEach((si) => {
          // Get Entity State
          const entityState = entityStates[(si as any).stateToken.name];

          // Check Modifier Type
          let modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType = si.modifierType;
          let snapshotData
          switch (si.modifierType) {
            case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD:
              modifierType = FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE;
              snapshotData = entityState.snapshots[si.followingSnapshotId as string] as FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateItemModel;
              break;
            case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE:
              snapshotData = entityState.snapshots[si.previousSnapshotId as string] as FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateItemModel;
              break;
            case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE:
              modifierType = FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD;
              snapshotData = entityState.snapshots[si.previousSnapshotId as string] as FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateItemModel;
              break;
            default:
              throw new FlatModels.ErrorControlModels.MedsurfError(
                "notification.type_not_supported",
                FlatModels.ErrorControlModels.ErrorLevel.enum.error,
                FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
                "LIBS.FlatStates.HistoryControlState.saveSnapshots"
              );
          }

          // Get Entity
          const entity = JSON.parse(snapshotData.itemJson);
          if (!entity) {
            throw new FlatModels.ErrorControlModels.MedsurfError(
              "notification.cannot_parse_entity",
              FlatModels.ErrorControlModels.ErrorLevel.enum.error,
              FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
              "LIBS.FlatStates.HistoryControlState.saveSnapshots"
            );
          }

          // Add entity to modified snapshots
          typedModifiedSnapshots[si.id] = {
            id: si.id,
            snapshotId: snapshot.id,
            stateToken: (si as any).stateToken.name as FlatModels.EntityControlModels.EntityQueryType,
            modifierType: modifierType,
            entity
          };
        });
      });

    // Create Array From Mapped Type
    const modifiedSnapshots: FlatModels.EntityControlModels.SaveSnapshotItem[] = Object.values(typedModifiedSnapshots);
    
    // Save entities
    if (modifiedSnapshots.length > 0) {
      return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
        mergeMap(() =>
          this.apiControlService.sendMessage(
            new FlatActions.HistoryControlActions.SaveSnapshotsRequest(modifiedSnapshots)
          )
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
        ),
        catchError(result => {
          this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
          
          // Return error
          return throwError(() => {
            return result;
          });
        })
      );
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.no_modified_entities",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.HistoryControlState.saveSnapshots"
      );
    }
  }

  @Action(FlatActions.HistoryControlActions.SaveSnapshotsSuccess)
  saveSnapshotsSuccess({getState, patchState}: StateContext<HistoryControlStateModel>,
                       {entityModifyResults}: FlatActions.HistoryControlActions.SaveSnapshotsSuccess) {
    // Format the results
    const snapshotsMap = new Map<string, string[]>();
    const entityTypesMap = new Map<string, FlatModels.EntityControlModels.SaveSnapshotItem[]>();
    for (const entityModifyResult of entityModifyResults) {
      // Snapshots Map
      if (entityModifyResult.snapshotId !== null && entityModifyResult.id !== null) {
        if (snapshotsMap.has(entityModifyResult.snapshotId)) {
          snapshotsMap.set(entityModifyResult.snapshotId, [...(snapshotsMap.get(entityModifyResult.snapshotId) as string[]), entityModifyResult.id])
        } else {
          snapshotsMap.set(entityModifyResult.snapshotId, [entityModifyResult.id]);
        }
      }

      // Entity Types Map
      if (entityTypesMap.has(entityModifyResult.stateToken)) {
        entityTypesMap.set(entityModifyResult.stateToken, [...(entityTypesMap.get(entityModifyResult.stateToken) as FlatModels.EntityControlModels.SaveSnapshotItem[]), entityModifyResult]);
      } else {
        entityTypesMap.set(entityModifyResult.stateToken, [entityModifyResult]);
      }
    }
    
    // Check Snapshot Map
    if (snapshotsMap.size > 0) {
      // Get state
      const past = structuredClone(getState().past);
      const future = structuredClone(getState().future);

      // Iterate Through Snapshot Map
      for (const [snapshotId,] of snapshotsMap) {
        // Past
        const past_snapshot = past.find(p => p.id === snapshotId);
        if (past_snapshot) {
          past_snapshot.saved = true;
          continue;
        }
        // Future
        const future_snapshot = future.find(f => f.id === snapshotId);
        if (future_snapshot) {
          future_snapshot.saved = false;
          continue;
        }

        // Error snapshot not found
        throw new FlatModels.ErrorControlModels.MedsurfError(
          "notification.no_snapshot_found",
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.HistoryControlState.saveSnapshotsSuccess"
        );
      }

      // Patch State
      patchState({
        past,
        future
      });
    }

    // Iterate Through Entity Types Map
    for (const [type, entityModifyResults] of entityTypesMap) {
      const entityMapping = entityMappings[type];
      if (entityMapping) {
        if (entityMapping.modifyEntity) {
          this.store.dispatch(new entityMapping.modifyEntitiesSuccess(entityModifyResults));
        } else {
          this.store.dispatch(new entityMapping.getEntitiesSuccess(entityModifyResults));
        }
      }
    }
  }

  @Action(FlatActions.HistoryControlActions.ClearHistoryForEntity)
  clearHistoryForEntity({getState, patchState}: StateContext<HistoryControlStateModel>,
                        {saveSnapshotItem}: FlatActions.HistoryControlActions.ClearHistoryForEntity) {
    // Get state
    const state = structuredClone(getState());

    // Clear Past
    const past = state.past.reduce((arr, past) => {
      let isDeleted = false;
      for (let i = 0; i < past.snapshotItems.length; i++)  {
        if (past.snapshotItems[i].entityId === saveSnapshotItem.entity.id && (past.snapshotItems[i] as any).stateToken.name === saveSnapshotItem.stateToken) {
          state.deleted = [past, ...state.deleted];
          isDeleted = true;
          break;
        }
      }
      if (!isDeleted) {
        arr.push(past);
      }
      return arr;
    }, [] as FlatModels.EntityControlModels.HistorySnapshot[]);

    // Clear Future
    const future = state.future.reduce((arr, future) => {
      let isDeleted = false;
      for (let i = 0; i < future.snapshotItems.length; i++)  {
        if (future.snapshotItems[i].entityId === saveSnapshotItem.entity.id && (future.snapshotItems[i] as any).stateToken.name === saveSnapshotItem.stateToken) {
          state.deleted = [future, ...state.deleted];
          isDeleted = true;
          break;
        }
      }
      if (!isDeleted) {
        arr.push(future);
      }
      return arr;
    }, [] as FlatModels.EntityControlModels.HistorySnapshot[])

    // Patch State
    patchState({
      past,
      future,
      deleted: state.deleted,
    });
  }

  @Action(FlatActions.HistoryControlActions.ClearHistoryForEntities)
  clearHistoryForEntities({getState, patchState}: StateContext<HistoryControlStateModel>,
                          {saveSnapshotItems}: FlatActions.HistoryControlActions.ClearHistoryForEntities) {
    // Get state
    const state = structuredClone(getState());

    // Create Save Snapshot Map
    const saveSnapshotItemsMap: Map<string, string[]> = new Map<string, string[]>();
    saveSnapshotItems.forEach((ssi) => {
      if (saveSnapshotItemsMap.has(ssi.stateToken)) {
        saveSnapshotItemsMap.set(ssi.stateToken, [...saveSnapshotItemsMap.get(ssi.stateToken) || [], ssi.entity.id]);
      } else {
        saveSnapshotItemsMap.set(ssi.stateToken, [ssi.entity.id]);
      }
    });

    // Clear Past
    const past = state.past.reduce((arr, past) => {
      let isDeleted = false;
      for (let i = 0; i < past.snapshotItems.length; i++)  {
        const pastSnapshotItem = past.snapshotItems[i];
        const saveSnapshotItemsMapStateTokenArray = saveSnapshotItemsMap.get((pastSnapshotItem as any).stateToken.name);
        if (Array.isArray(saveSnapshotItemsMapStateTokenArray) && saveSnapshotItemsMapStateTokenArray.includes(pastSnapshotItem.entityId)) {
          state.deleted = [past, ...state.deleted];
          isDeleted = true;
          break;
        }
      }
      if (!isDeleted) {
        arr.push(past);
      }
      return arr;
    }, [] as FlatModels.EntityControlModels.HistorySnapshot[]);

    // Clear Future
    const future = state.future.reduce((arr, future) => {
      let isDeleted = false;
      for (let i = 0; i < future.snapshotItems.length; i++)  {
        const futureSnapshotItem = future.snapshotItems[i];
        const saveSnapshotItemsMapStateTokenArray = saveSnapshotItemsMap.get((futureSnapshotItem as any).stateToken.name);
        if (Array.isArray(saveSnapshotItemsMapStateTokenArray) && saveSnapshotItemsMapStateTokenArray.includes(futureSnapshotItem.entityId)) {
          state.deleted = [future, ...state.deleted];
          isDeleted = true;
          break;
        }
      }
      if (!isDeleted) {
        arr.push(future);
      }
      return arr;
    }, [] as FlatModels.EntityControlModels.HistorySnapshot[]);

    // Patch State
    patchState({
      past,
      future,
      deleted: state.deleted,
    });
  }

  //</editor-fold>
}
